/**
 * 配送希望日時のルール判定（UI 非依存の純粋関数群）
 *
 * cart-vendor-delivery.js から「選択可能な日付/時間帯を決めるロジック」だけを
 * 抽出したもの。抽出は移植のみで、判定順・holidayAdd の挙動・年末年始
 * フォールバックの分岐構造は元実装をそのまま保持している。
 * （唯一の差分は、テスト可能にするため基準日時 now を引数化した点。
 *   元実装はループ内で毎回 new Date() していたが、これはスナップショットを
 *   使い回す形に変更している。差はサブミリ秒で、判定結果に影響しない。）
 *
 * 公開先: window.DelishDeliveryRules
 */
(function () {
  var DAY_NAMES = ['日', '月', '火', '水', '木', '金', '土'];
  var WEEK_KEYS = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

  var DEFAULT_DELIVERY_TIMES = [
    '午前中',
    '12:00 ~ 14:00',
    '14:00 ~ 16:00',
    '16:00 ~ 18:00',
    '18:00 ~ 20:00',
    '19:00 ~ 21:00',
  ];

  /**
   * 年末年始の一括配送不可期間。
   * vendor 個別の vendor_unavailable_period が metaobject に設定されている場合は
   * そちらが優先され、この期間は適用されない（現行踏襲）。
   *
   * TODO: 毎年ここを書き換える運用は事故る（実際に 2025/2026 のまま放置されていた）。
   *       metaobject 側の共通レコードに寄せてハードコードを廃止する。CLAUDE.md 参照。
   */
  var YEAR_END_BLOCK = {
    start: { year: 2026, month: 11, date: 26 }, // 2026/12/26
    end: { year: 2027, month: 0, date: 8 }, //     2027/1/8
  };

  /** 元実装 `${y}/${m+1}/${d} (${曜})` と完全に同一の出力 */
  function formatDeliveryDateLabel(date) {
    return (
      date.getFullYear() +
      '/' +
      (date.getMonth() + 1) +
      '/' +
      date.getDate() +
      ' (' +
      DAY_NAMES[date.getDay()] +
      ')'
    );
  }

  /**
   * 保存済みラベル '2026/8/10 (月)' を解析する。
   * 解析できない場合は null。
   */
  function parseDeliveryDateLabel(label) {
    if (typeof label !== 'string') return null;
    var m = label.trim().match(/^(\d{4})\/(\d{1,2})\/(\d{1,2})\s*\(.\)$/);
    if (!m) return null;
    var year = Number(m[1]);
    var month = Number(m[2]);
    var date = Number(m[3]);
    var d = new Date(year, month - 1, date);
    if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== date) return null;
    return d;
  }

  /** 警告文に出す「元のご指定」用。曜日を落として YYYY/M/D にする */
  function toPlainDateText(label) {
    var d = parseDeliveryDateLabel(label);
    if (!d) return String(label == null ? '' : label);
    return d.getFullYear() + '/' + (d.getMonth() + 1) + '/' + d.getDate();
  }

  function isYearEndBlocked(date, block) {
    var b = block || YEAR_END_BLOCK;
    return (
      (date.getFullYear() === b.start.year &&
        date.getMonth() === b.start.month &&
        date.getDate() >= b.start.date) ||
      (date.getFullYear() === b.end.year &&
        date.getMonth() === b.end.month &&
        date.getDate() <= b.end.date)
    );
  }

  /**
   * 選択可能な日付ラベルの配列を返す。
   *
   * @param {object} data              /users/shipping のレスポンス
   *                                   { min_schedule_days, max_schedule_days, holidays: ['SUNDAY',...] }
   * @param {object|null} unavailable  { start: Date, end: Date } または null
   * @param {Date} [baseNow]           基準日時（省略時は現在時刻）
   * @param {object} [yearEndBlock]    年末年始ブロック（省略時は YEAR_END_BLOCK。テスト用）
   * @returns {string[]}
   */
  function buildSelectableDates(data, unavailable, baseNow, yearEndBlock) {
    var labels = [];
    if (!data) return labels;

    var base = baseNow instanceof Date ? baseNow : new Date();
    var holidays = (data.holidays || [])
      .map(function (h) {
        return WEEK_KEYS.indexOf(h);
      })
      .filter(function (i) {
        return i >= 0;
      });
    var holidayAdd = 0;

    for (var i = 0; i <= data.max_schedule_days; i++) {
      var now = new Date(base.getTime());
      now.setDate(now.getDate() + i);

      if (i < data.min_schedule_days + holidayAdd) {
        if (holidays.indexOf(now.getDay()) !== -1) holidayAdd += 1;
        continue;
      }

      // 個別設定がある場合はそちらのみを適用（年末年始ハードコードは無視）
      if (unavailable) {
        if (
          now.getTime() >= unavailable.start.getTime() &&
          now.getTime() <= unavailable.end.getTime()
        ) {
          continue;
        }
      } else {
        if (isYearEndBlocked(now, yearEndBlock)) continue;
      }

      labels.push(formatDeliveryDateLabel(now));
    }

    return labels;
  }

  /**
   * 選択可能な時間帯の配列を返す。
   * 配送業者がヤマト運輸のみの場合は '12:00 ~ 14:00' を除外（現行踏襲）。
   */
  function buildSelectableTimes(data) {
    var times = DEFAULT_DELIVERY_TIMES.slice();
    if (
      data &&
      Array.isArray(data.tracking_companies) &&
      data.tracking_companies.length === 1 &&
      data.tracking_companies[0] === 'ヤマト運輸'
    ) {
      times.splice(1, 1);
    }
    return times;
  }

  /** 保存値がまだ選択肢に存在するか。空文字/未指定は「検証対象外＝OK」 */
  function isStillSelectable(savedValue, allowedValues) {
    if (savedValue == null || savedValue === '') return true;
    return allowedValues.indexOf(savedValue) !== -1;
  }

  /** vendor が日時指定そのものに対応しているか（元実装の判定を踏襲） */
  function supportsDeliveryDate(data) {
    return !!(data && (data.min_schedule_days || data.max_schedule_days));
  }

  var api = {
    DAY_NAMES: DAY_NAMES,
    WEEK_KEYS: WEEK_KEYS,
    DEFAULT_DELIVERY_TIMES: DEFAULT_DELIVERY_TIMES,
    YEAR_END_BLOCK: YEAR_END_BLOCK,
    formatDeliveryDateLabel: formatDeliveryDateLabel,
    parseDeliveryDateLabel: parseDeliveryDateLabel,
    toPlainDateText: toPlainDateText,
    isYearEndBlocked: isYearEndBlocked,
    buildSelectableDates: buildSelectableDates,
    buildSelectableTimes: buildSelectableTimes,
    isStillSelectable: isStillSelectable,
    supportsDeliveryDate: supportsDeliveryDate,
  };

  if (typeof window !== 'undefined') window.DelishDeliveryRules = api;
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
})();
